/* Swell Scenes Personalizer — storefront editor.
 *
 * Opens an editor modal on the product page, loads the layout spec + text-free
 * base art from the app backend (via App Proxy), and renders a real-time canvas
 * preview that MIRRORS the server compositor (compositor.py): same scrim,
 * uppercase, em-based tracking, fit-to-width and matte logic, so what the
 * customer sees closely matches the printed file. On Add to cart it validates
 * server-side and stores the config as line-item properties.
 */
(function () {
  "use strict";

  // Re-execution guard: this file is loaded both site-wide (header CTA) and on
  // product pages (the personalizer section). Loading it twice is fine — the
  // second execution no-ops here, and boot()'s per-element guards prevent any
  // double binding regardless of which tag runs first.
  if (window.__swellPzScriptRan) return;
  window.__swellPzScriptRan = true;

  // Size the editor opens on when the product offers it (else the product's own
  // default_size from /spec is used).
  var PREFERRED_DEFAULT_SIZE = "12x18";
  // Preferred "Gap from heading" default + slider minimum. Mirrors travel.json
  // (the source of truth); applied client-side so they take effect without a
  // backend redeploy. All values here are within the server's validation range.
  var GAP_DEFAULT_PX = 60;
  var GAP_MIN_PX = -60;

  // Apply the preferred UI overrides onto a freshly-fetched spec so all the
  // generic downstream code (state init, slider bounds) picks them up.
  function applyPreferredSpecOverrides(spec) {
    if (!spec) return;
    if (spec.defaults) spec.defaults.block_gap_px = GAP_DEFAULT_PX;
    if (spec.limits && spec.limits.block_gap_px) spec.limits.block_gap_px.min = GAP_MIN_PX;
  }

  // True when the page was opened with ?customize=1 (or #customize) — the deep
  // link the site-wide Personalize search uses to jump straight into the editor.
  function wantsAutoOpen() {
    try {
      var p = new URLSearchParams(window.location.search);
      var c = (p.get("customize") || "").toLowerCase();
      if (c === "1" || c === "true" || c === "yes") return true;
    } catch (e) {}
    return (window.location.hash || "").toLowerCase() === "#customize";
  }

  function $(root, sel) { return root.querySelector(sel); }
  function el(tag, cls) { var e = document.createElement(tag); if (cls) e.className = cls; return e; }
  function hexOf(list, id) { var f = list.find(function (x) { return x.id === id; }); return f ? f.hex : "#000"; }
  function resolveColor(state, idKey, customKey, colors) {
    if (state[customKey]) return state[customKey];
    return hexOf(colors, state[idKey]);
  }
  function scaledTypo(spec, H, state) {
    var typo = (spec.layout && spec.layout.typography) || {};
    var ref = typo.reference_height_px || 5000;
    var scale = H / ref;
    var fsLim = (spec.limits && spec.limits.font_size_px) || {};
    var fsMin = fsLim.min || 60;
    var headingPx = (state && state.heading_font_size_px) || typo.heading_font_px || 200;
    var subPx = (state && state.subheading_font_size_px) || typo.subheading_font_px || 90;
    var headTrack = (state && state.heading_tracking != null) ? state.heading_tracking : (typo.heading_tracking || 0.012);
    var subTrack = (state && state.subheading_tracking != null) ? state.subheading_tracking : (typo.subheading_tracking || 0.10);
    return {
      headingStart: Math.round(headingPx * scale),
      headingMin: Math.round(fsMin * scale),
      subStart: Math.round(subPx * scale),
      subMin: Math.round(fsMin * scale),
      gap: Math.round(((state && state.block_gap_px != null) ? state.block_gap_px : (typo.gap_px || 18)) * scale),
      margin: Math.round((typo.edge_margin_px || 280) * scale),
      boxX: typo.box_x_frac || 0.08,
      boxW: typo.box_w_frac || 0.84,
      headingTracking: headTrack,
      subTracking: subTrack,
      headingMaxLines: typo.heading_max_lines || 2,
      headingLineSpacing: typo.heading_line_spacing || 1.0
    };
  }
  function familyOf(fonts, id) { var f = fonts.find(function (x) { return x.id === id; }); return f ? f.family : "sans-serif"; }
  function fontById(fonts, id) { return fonts.find(function (x) { return x.id === id; }) || null; }
  function fontWeights(font) { return (font && font.weights && font.weights.length) ? font.weights : null; }
  function defaultWeightId(font) {
    var ws = fontWeights(font);
    if (!ws) return null;
    return font.default_weight || ws[0].id;
  }
  function weightEntry(font, weightId) {
    var ws = fontWeights(font);
    if (!ws) return null;
    return ws.filter(function (w) { return w.id === weightId; })[0] ||
           ws.filter(function (w) { return w.id === defaultWeightId(font); })[0] || ws[0];
  }
  // Numeric CSS font-weight for the selected (font, weight). Static families map
  // each weight to its own @font-face; variable families interpolate via a range.
  function weightNumFor(fonts, fontId, weightId) {
    var f = fontById(fonts, fontId);
    if (!f) return 400;
    var w = weightEntry(f, weightId);
    if (w) return w.weight || 400;
    return f.weight || 400;
  }
  function fontsForRole(fonts, role) {
    return fonts.filter(function (f) {
      if (!f.roles || !f.roles.length) return true;
      return f.roles.indexOf(role) >= 0;
    });
  }
  function fontUppercase(fonts, fontId, layoutDefault) {
    var f = fonts.find(function (x) { return x.id === fontId; });
    if (f && Object.prototype.hasOwnProperty.call(f, "uppercase")) return !!f.uppercase;
    return layoutDefault !== false;
  }
  // Map a Printful style name (e.g. "Walnut Floating Frame Canvas") to a frame
  // preview kind + tone. Purely decorative (CSS around the canvas); never baked
  // into the saved print/cart image.
  function frameSpec(styleName) {
    if (!styleName) return { kind: "none", tone: "none" };
    var lower = String(styleName).toLowerCase();
    if (lower.indexOf("print only") >= 0 || lower === "print") return { kind: "none", tone: "none" };
    var kind = lower.indexOf("floating") >= 0 ? "floating" : (lower.indexOf("frame") >= 0 ? "print" : "none");
    var tone = "black";
    if (lower.indexOf("walnut") >= 0) tone = "walnut";
    else if (lower.indexOf("wood") >= 0) tone = "wood";
    else if (lower.indexOf("white") >= 0) tone = "white";
    else if (lower.indexOf("black") >= 0) tone = "black";
    return { kind: kind, tone: tone };
  }
  function ratioForState(spec, state) {
    var size = state.size || state.ratio;
    if (spec.sizes && spec.sizes[size]) return spec.sizes[size].ratio;
    return size;
  }
  function previewAspect(spec, state) {
    var ratio = ratioForState(spec, state);
    var px = spec.print[ratio] && spec.print[ratio].px;
    if (px) return px[0] / px[1];
    var size = state.size || state.ratio;
    if (spec.sizes && spec.sizes[size] && spec.sizes[size].inches) {
      var inches = spec.sizes[size].inches;
      return inches[0] / inches[1];
    }
    return 0.8;
  }
  function absoluteProxyUrl(proxyBase, pathOrUrl) {
    if (!pathOrUrl) return "";
    if (pathOrUrl.indexOf("http") === 0) return pathOrUrl;
    var base = (proxyBase || "").replace(/\/$/, "");
    var path = pathOrUrl.indexOf("/") === 0 ? pathOrUrl : "/" + pathOrUrl;
    if (base.indexOf("http") === 0) return base + path;
    if (base.indexOf("/") === 0) return window.location.origin + base + path;
    return path;
  }

  function loadCustomFonts(fonts, proxyBase) {
    // Inject @font-face rules pointing at the app backend so the canvas preview
    // uses the same brand fonts as the print compositor. Each family gets a
    // prefixed name (e.g. "SWKiona") to avoid collisions with any theme fonts.
    var style = document.getElementById("swell-pz-fonts");
    if (!style) {
      style = document.createElement("style");
      style.id = "swell-pz-fonts";
      document.head.appendChild(style);
    }
    function faceFor(family, url, weightDesc) {
      var ext = url.toLowerCase().indexOf(".otf") >= 0 ? "opentype" : "truetype";
      return "@font-face { font-family: '" + family + "'; font-weight: " + weightDesc +
        "; font-style: normal; src: url('" + url + "') format('" + ext + "'); }";
    }
    var rules = [];
    fonts.forEach(function (f) {
      var ws = f.weights && f.weights.length ? f.weights : null;
      if (ws && f.variable) {
        // Variable font: one face, weight range — the browser interpolates.
        var lo = ws[0].weight, hi = ws[0].weight;
        ws.forEach(function (w) { lo = Math.min(lo, w.weight); hi = Math.max(hi, w.weight); });
        var vurl = proxyBase + "/fonts/" + encodeURIComponent(f.file);
        rules.push(faceFor(f.family, vurl, lo + " " + hi));
      } else if (ws) {
        // Static family: one face per weight (same family name, distinct file).
        ws.forEach(function (w) {
          var file = w.file || f.file;
          rules.push(faceFor(f.family, proxyBase + "/fonts/" + encodeURIComponent(file), w.weight || 400));
        });
      } else {
        rules.push(faceFor(f.family, proxyBase + "/fonts/" + encodeURIComponent(f.file), f.weight || 400));
      }
    });
    // Only declare the @font-face rules here — do NOT block startup on downloading
    // every font (a single variable font can be ~1MB over the app proxy). Each
    // render lazily loads only the fonts the current selection needs and repaints
    // once they arrive (Editor.ensureFonts), so the correct font shows every time.
    style.textContent = rules.join("\n");
    return Promise.resolve();
  }

  // ── Text fitting (mirrors compositor._fit_font_size, em-based tracking) ──────
  function fitText(ctx, text, family, weight, maxW, startSize, minSize, trackingFrac) {
    var step = Math.max(1, Math.round(startSize * 0.03));
    for (var size = startSize; size >= minSize; size -= step) {
      ctx.font = weight + " " + size + 'px "' + family + '", sans-serif';
      var tracking = trackingFrac * size;
      var w = ctx.measureText(text).width + tracking * Math.max(0, text.length - 1);
      if (w <= maxW) return { size: size, tracking: tracking };
    }
    return { size: minSize, tracking: trackingFrac * minSize };
  }

  // ── Multi-line heading (mirrors compositor._layout_heading / _fit_lines) ─────
  // Try 1..maxLines layouts, keep the one with the LARGEST font size (ties prefer
  // fewer lines) so short headings stay single-line and long ones wrap to match
  // the printed poster. Must stay identical to the Python compositor.
  function measureLine(ctx, text, family, weight, size, trackingFrac) {
    ctx.font = weight + " " + size + 'px "' + family + '", sans-serif';
    var tracking = trackingFrac * size;
    return ctx.measureText(text).width + tracking * Math.max(0, text.length - 1);
  }
  function splitBalanced(ctx, words, n, family, weight, probeSize, trackingFrac) {
    if (n <= 1 || words.length <= 1) return [words.join(" ")];
    if (n === 2) {
      var best = null;
      for (var k = 1; k < words.length; k++) {
        var a = words.slice(0, k).join(" "), b = words.slice(k).join(" ");
        var widest = Math.max(measureLine(ctx, a, family, weight, probeSize, trackingFrac),
                              measureLine(ctx, b, family, weight, probeSize, trackingFrac));
        if (best === null || widest < best.w) best = { w: widest, lines: [a, b] };
      }
      return best.lines;
    }
    var per = Math.ceil(words.length / n), out = [];
    for (var i = 0; i < words.length; i += per) out.push(words.slice(i, i + per).join(" "));
    return out;
  }
  function fitLines(ctx, lines, family, weight, maxW, startSize, minSize, trackingFrac) {
    var step = Math.max(1, Math.round(startSize * 0.03));
    for (var size = startSize; size >= minSize; size -= step) {
      var tracking = trackingFrac * size, ok = true;
      for (var i = 0; i < lines.length; i++) {
        if (measureLine(ctx, lines[i], family, weight, size, trackingFrac) > maxW) { ok = false; break; }
      }
      if (ok) return { size: size, tracking: tracking };
    }
    return { size: minSize, tracking: trackingFrac * minSize };
  }
  function layoutHeading(ctx, text, family, weight, maxW, startSize, minSize, trackingFrac, maxLines) {
    var words = String(text).split(/\s+/).filter(Boolean);
    var best = null;
    for (var n = 1; n <= Math.max(1, maxLines); n++) {
      if (n > words.length) break;
      var lines = splitBalanced(ctx, words, n, family, weight, startSize, trackingFrac);
      var fit = fitLines(ctx, lines, family, weight, maxW, startSize, minSize, trackingFrac);
      if (best === null || fit.size > best.size) best = { size: fit.size, tracking: fit.tracking, lines: lines };
    }
    return best;
  }

  // Distance from the top of a line to its alphabetic baseline, derived from the
  // font's own declared metrics. We do NOT use textBaseline "top": Safari and
  // Chrome position the "top"/"hanging" baselines from different metric tables,
  // so the same canvas renders text at slightly different heights per browser —
  // which is what made the heading↔subheading gap look smaller on mobile than on
  // desktop. The alphabetic baseline is defined consistently everywhere, and
  // fontBoundingBoxAscent comes straight from the font file, so top-of-line
  // placement becomes identical across devices.
  function topToBaseline(ctx, size) {
    try {
      var m = ctx.measureText("Hg");
      if (m && typeof m.fontBoundingBoxAscent === "number" && m.fontBoundingBoxAscent > 0) {
        return m.fontBoundingBoxAscent;
      }
    } catch (e) { /* fall through */ }
    return size * 0.8;
  }

  // Returns the drawn text's bounding box {x, y, w, h} in canvas space so the
  // caller can build click hit-regions (tap the heading/subheading in the
  // preview to jump to its input).
  function drawTracked(ctx, text, family, weight, size, tracking, color, align, boxLeft, boxW, y) {
    ctx.font = weight + " " + size + 'px "' + family + '", sans-serif';
    ctx.fillStyle = color;
    ctx.textBaseline = "alphabetic";
    var baseline = y + topToBaseline(ctx, size);   // y is the top of the line
    var total = ctx.measureText(text).width + tracking * Math.max(0, text.length - 1);
    var x = align === "center" ? boxLeft + (boxW - total) / 2
          : align === "right" ? boxLeft + (boxW - total) : boxLeft;
    var xStart = x;
    for (var i = 0; i < text.length; i++) {
      var ch = text[i];
      ctx.fillText(ch, x, baseline);
      x += ctx.measureText(ch).width + tracking;
    }
    return { x: xStart, y: y, w: total, h: size * 1.15 };
  }

  function Editor(root) {
    this.root = root;
    this.proxyBase = root.getAttribute("data-proxy-base") || "/apps/personalizer";
    this.handle = root.getAttribute("data-handle");
    this.fallbackVariant = root.getAttribute("data-fallback-variant");
    try { this.variantMap = JSON.parse(root.getAttribute("data-variant-map") || "{}"); }
    catch (e) { this.variantMap = {}; }

    this.modal = $(root, "[data-swell-modal]");
    this.canvas = $(root, "[data-swell-canvas]");
    // Frame effect goes on a wrapper div (borders/padding/gradients render
    // reliably there; a <canvas> is a replaced element whose bitmap fills its
    // content box, so a CSS border on it is inconsistent across browsers).
    this.frame = $(root, "[data-swell-frame]") || this.canvas;
    this.controls = $(root, "[data-swell-controls]");
    this.statusEl = $(root, "[data-swell-status]");
    this.errorEl = $(root, "[data-swell-error]");
    this.addBtn = $(root, "[data-swell-add]");

    this.baseCache = {};   // ratio -> HTMLImageElement
    this.spec = null;
    this.state = null;
    this.loadError = null;
    this.loaderEl = null;
    this.loaderText = null;
    this.controlsLoaderEl = null;
    this.controlsLoaderText = null;
    this.loading = false;
  }

  Editor.prototype.setLoading = function (on, msg) {
    this.loading = !!on;
    this.addBtn.disabled = on;
    if (on) {
      this.showLoader(msg || "Loading…");
      this.showControlsLoader(msg || "Loading options…");
    } else {
      this.hideLoader();
      this.hideControlsLoader();
    }
  };

  Editor.prototype.showControlsLoader = function (msg) {
    if (!this.controlsLoaderEl) {
      this.controlsLoaderEl = el("div", "swell-pz-controls__loader");
      this.controlsLoaderEl.setAttribute("data-swell-controls-loader", "");
      this.controlsLoaderEl.appendChild(el("div", "swell-pz-spinner"));
      this.controlsLoaderText = el("span", "swell-pz-controls__loader-text");
      this.controlsLoaderEl.appendChild(this.controlsLoaderText);
      this.controls.insertBefore(this.controlsLoaderEl, this.controls.firstChild);
    }
    this.controlsLoaderText.textContent = msg || "Loading options…";
    this.controlsLoaderEl.hidden = false;
    this.controls.classList.add("swell-pz-controls--loading");
  };

  Editor.prototype.hideControlsLoader = function () {
    if (this.controlsLoaderEl) this.controlsLoaderEl.hidden = true;
    this.controls.classList.remove("swell-pz-controls--loading");
  };

  Editor.prototype.showLoader = function (msg) {
    if (!this.loaderEl) {
      var preview = this.canvas.parentElement;
      this.loaderEl = el("div", "swell-pz-preview__loader");
      this.loaderEl.setAttribute("data-swell-loader", "");
      this.loaderEl.appendChild(el("div", "swell-pz-spinner"));
      this.loaderText = el("span", "swell-pz-preview__loader-text");
      this.loaderEl.appendChild(this.loaderText);
      preview.appendChild(this.loaderEl);
    }
    this.loaderText.textContent = msg || "Loading…";
    this.loaderEl.hidden = false;
    this.canvas.classList.add("swell-pz-preview--loading");
  };

  Editor.prototype.hideLoader = function () {
    if (this.loaderEl) this.loaderEl.hidden = true;
    this.canvas.classList.remove("swell-pz-preview--loading");
  };

  Editor.prototype.init = function () {
    var self = this;
    // Stay hidden until /spec confirms this product is personalizable. The button
    // is only relocated/revealed on success, so non-personalizable products (404)
    // never show a dead "Customize" button when this section is on the default
    // product template.
    self.root.style.display = "none";
    self.bindOpenClose();
    self.setLoading(true, "Loading editor…");
    fetch(this.proxyBase + "/spec?handle=" + encodeURIComponent(this.handle))
      .then(function (r) {
        if (r.status === 404) { self.root.style.display = "none"; return null; }
        if (!r.ok) throw new Error("Could not reach personalization backend (" + r.status + ")");
        return r.json();
      })
      .then(function (spec) {
        if (!spec) {
          self.setLoading(false);
          return;
        }
        self.spec = spec;
        applyPreferredSpecOverrides(spec);
        if (spec.variant_map && Object.keys(spec.variant_map).length) {
          self.variantMap = spec.variant_map;
        }
        self.state = Object.assign({ size: spec.default_size || spec.default_ratio, style: spec.default_style || "Print Only" }, spec.defaults, spec.text_defaults || {});
        // Prefer 12x18 as the opening size whenever the product offers it, else
        // fall back to the product's own default_size (from /spec).
        var sizeIds0 = spec.size_ids || spec.ratios || [];
        if (sizeIds0.indexOf(PREFERRED_DEFAULT_SIZE) >= 0) self.state.size = PREFERRED_DEFAULT_SIZE;
        // Keep the default style valid for the default size (a product may not
        // offer every frame style at the opening size) — prefer Black Frame Print.
        var styles0 = self.getStylesForSize(self.state.size);
        if (styles0.indexOf(self.state.style) < 0) {
          self.state.style = styles0.indexOf("Black Frame Print") >= 0 ? "Black Frame Print" : styles0[0];
        }
        return loadCustomFonts(spec.options.fonts, self.proxyBase);
      })
      .then(function () {
        if (!self.spec) return;
        self.root.style.display = "";
        self.relocateButton();
        self.buildControls();
        self.hideControlsLoader();
        self.addBtn.disabled = false;
        if (!self.modal.hidden) self.render();
        else self.hideLoader();
        // Deep-link from the site-wide Personalize search: open the editor now
        // that the spec + controls are ready.
        if (!self.__autoOpened && wantsAutoOpen()) {
          self.__autoOpened = true;
          self.open();
        }
      })
      .catch(function (e) {
        console.error("[personalizer]", e);
        self.loadError = e.message || "Backend unreachable";
        self.setLoading(false);
        if (!self.modal.hidden) {
          self.statusEl.textContent = self.loadError;
        }
      });
  };

  Editor.prototype.relocateButton = function () {
    this.openBtn = this.root.querySelector('[data-swell-open]');
    if (!this.openBtn) return;
    var target = document.querySelector('.product-form__buttons');
    if (!target) target = document.querySelector('.product-form__submit');
    if (!target) return;
    target.parentNode.insertBefore(this.openBtn, target);
  };

  Editor.prototype.bindOpenClose = function () {
    var self = this;
    (this.openBtn || $(this.root, "[data-swell-open]")).addEventListener("click", function () { self.open(); });
    this.root.querySelectorAll("[data-swell-close]").forEach(function (b) {
      b.addEventListener("click", function () { self.close(); });
    });
    this.addBtn.addEventListener("click", function () { self.addToCart(); });
    // Tap the heading/subheading in the preview to jump to its text field.
    if (this.canvas) this.canvas.addEventListener("click", function (e) { self.onCanvasClick(e); });
  };

  Editor.prototype.open = function () {
    this.modal.hidden = false;
    this.modal.setAttribute("aria-hidden", "false");
    if (this.loadError) {
      this.setLoading(false);
      this.statusEl.textContent = this.loadError;
      return;
    }
    if (!this.spec) {
      this.setLoading(true, "Loading editor…");
      this.statusEl.textContent = "";
      return;
    }
    if (!this.controls.querySelector(".swell-pz-section")) {
      this.showControlsLoader("Loading options…");
    }
    this.render();
  };
  Editor.prototype.close = function () {
    this.modal.hidden = true;
    this.modal.setAttribute("aria-hidden", "true");
    this.hideLoader();
  };

  Editor.prototype.set = function (key, value) {
    this.state[key] = value;
    this.errorEl.textContent = "";
    this.render();
  };

  Editor.prototype.setFontFamily = function (role, fontId) {
    var fontKey = role + "_font", weightKey = role + "_font_weight";
    this.state[fontKey] = fontId;
    var f = fontById(this.spec.options.fonts, fontId);
    var ws = fontWeights(f);
    this.state[weightKey] = ws ? defaultWeightId(f) : null;
    this.errorEl.textContent = "";
    this.buildControls();   // weight picker appears/updates for the new family
    this.render();
  };

  Editor.prototype.setSize = function (size) {
    this.state.size = size;
    // Not every size offers every frame style (e.g. 5x7 is print-only), so keep
    // the selected style valid — prefer Black Frame Print, else the first option.
    var styles = this.getStylesForSize(size);
    if (styles.indexOf(this.state.style) < 0) {
      this.state.style = styles.indexOf("Black Frame Print") >= 0 ? "Black Frame Print" : styles[0];
    }
    this.errorEl.textContent = "";
    this.buildControls();
    this.render();
  };

  var STYLE_ORDER = [
    "Print Only", "Black Frame Print", "White Frame Print", "Wood Frame Print",
    "Black Floating Frame Canvas", "Walnut Floating Frame Canvas", "White Floating Frame Canvas"
  ];

  Editor.prototype.getStylesForSize = function (size) {
    var vm = this.variantMap;
    if (!vm || !vm[size]) return ["Print Only"];
    if (typeof vm[size] === "object" && !Array.isArray(vm[size])) {
      var keys = Object.keys(vm[size]);
      keys.sort(function (a, b) {
        var ia = STYLE_ORDER.indexOf(a), ib = STYLE_ORDER.indexOf(b);
        if (ia < 0) ia = 999;
        if (ib < 0) ib = 999;
        return ia - ib;
      });
      return keys;
    }
    return ["Print Only"];
  };

  Editor.prototype.buildControls = function () {
    var self = this, spec = this.spec, opts = spec.options, s = this.state;
    this.controls.innerHTML = "";
    this.fieldInputs = {};   // role -> text input, for tap-to-edit from the preview

    function field(labelText) {
      var f = el("div", "swell-pz-field");
      var l = el("label"); l.textContent = labelText; f.appendChild(l);
      return f;
    }
    function textInput(key, max) {
      var inp = el("input"); inp.type = "text"; inp.value = s[key] || ""; if (max) inp.maxLength = max;
      inp.addEventListener("input", function () { self.set(key, inp.value); });
      self.fieldInputs[key] = inp;
      return inp;
    }
    function select(key, items, labelKey, valueKey) {
      var sel = el("select");
      items.forEach(function (it) {
        var o = el("option"); o.value = it[valueKey]; o.textContent = it[labelKey];
        if (it[valueKey] === s[key]) o.selected = true;
        sel.appendChild(o);
      });
      sel.addEventListener("change", function () { self.set(key, sel.value); });
      return sel;
    }
    function fontControl(role) {
      var fontKey = role + "_font", weightKey = role + "_font_weight";
      var wrap = el("div", "swell-pz-fontctl");
      var fontSel = el("select");
      fontsForRole(opts.fonts, role).forEach(function (f) {
        var o = el("option"); o.value = f.id; o.textContent = f.label;
        if (f.id === s[fontKey]) o.selected = true;
        fontSel.appendChild(o);
      });
      fontSel.addEventListener("change", function () { self.setFontFamily(role, fontSel.value); });
      wrap.appendChild(fontSel);

      var f = fontById(opts.fonts, s[fontKey]);
      var ws = fontWeights(f);
      if (ws && ws.length > 1) {
        if (!weightEntry(f, s[weightKey])) s[weightKey] = defaultWeightId(f);
        var wSel = el("select", "swell-pz-weight");
        ws.forEach(function (w) {
          var o = el("option"); o.value = w.id; o.textContent = w.label;
          if (w.id === s[weightKey]) o.selected = true;
          wSel.appendChild(o);
        });
        wSel.addEventListener("change", function () { self.set(weightKey, wSel.value); });
        wrap.appendChild(wSel);
      }
      return wrap;
    }
    function swatches(idKey, customKey, colors) {
      var wrap = el("div", "swell-pz-swatches");
      colors.forEach(function (c) {
        var b = el("button", "swell-pz-swatch");
        b.type = "button";
        b.style.background = c.hex; b.title = c.label;
        var active = !s[customKey] && s[idKey] === c.id;
        b.setAttribute("aria-pressed", String(active));
        b.addEventListener("click", function () {
          self.state[customKey] = null;
          self.set(idKey, c.id);
          wrap.querySelectorAll(".swell-pz-swatch").forEach(function (x) { x.setAttribute("aria-pressed", "false"); });
          b.setAttribute("aria-pressed", "true");
          preview.style.background = c.hex;
        });
        wrap.appendChild(b);
      });

      var pickerWrap = el("div", "swell-pz-picker-btn-wrap");
      var pickerBtn = el("button", "swell-pz-picker-btn");
      pickerBtn.type = "button";
      pickerBtn.title = "Pick a custom color";
      pickerBtn.setAttribute("aria-label", "Pick a custom color");
      var preview = el("span", "swell-pz-picker-btn__preview");
      preview.style.background = resolveColor(s, idKey, customKey, colors);
      var icon = el("span", "swell-pz-picker-btn__icon");
      icon.innerHTML = "<svg width=\"14\" height=\"14\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" aria-hidden=\"true\"><path d=\"M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z\"/></svg>";
      var lbl = el("span", "swell-pz-picker-btn__label");
      lbl.textContent = "Custom";
      pickerBtn.appendChild(preview);
      pickerBtn.appendChild(icon);
      pickerBtn.appendChild(lbl);

      var picker = el("input");
      picker.type = "color";
      picker.className = "swell-pz-color-picker";
      picker.value = resolveColor(s, idKey, customKey, colors);
      picker.tabIndex = -1;
      picker.addEventListener("input", function () {
        self.state[customKey] = picker.value.toUpperCase();
        preview.style.background = picker.value;
        wrap.querySelectorAll(".swell-pz-swatch").forEach(function (x) { x.setAttribute("aria-pressed", "false"); });
        self.render();
      });
      pickerBtn.addEventListener("click", function () { picker.click(); });
      pickerWrap.appendChild(pickerBtn);
      pickerWrap.appendChild(picker);
      wrap.appendChild(pickerWrap);
      return wrap;
    }
    function fontSizeRange(key, labelText) {
      var fsLim = (spec.limits && spec.limits[key]) || (spec.limits && spec.limits.font_size_px) || { min: 60, max: 360 };
      // Use != null (not ||) so a valid 0 / negative min or default isn't dropped
      // — e.g. block_gap_px can legitimately be negative to tighten line spacing.
      var min = fsLim.min != null ? fsLim.min : 60;
      var max = fsLim.max != null ? fsLim.max : 360;
      var val = s[key];
      if (val == null) val = (spec.defaults && spec.defaults[key] != null) ? spec.defaults[key] : min;
      var num = Number(val);
      if (isNaN(num)) num = min;   // don't use `|| min` — a valid 0 must survive
      val = Math.max(min, Math.min(max, num));
      s[key] = val;

      var f = el("div", "swell-pz-field swell-pz-field--size");
      var l = el("label");
      l.textContent = labelText;
      var valSpan = el("span", "swell-pz-size-val");
      valSpan.textContent = val + " px";
      l.appendChild(valSpan);
      f.appendChild(l);

      var inp = el("input");
      inp.type = "range";
      inp.min = String(min);
      inp.max = String(max);
      inp.step = "1";
      inp.value = String(val);
      inp.className = "swell-pz-range";
      inp.addEventListener("input", function () {
        var n = parseInt(inp.value, 10);
        valSpan.textContent = n + " px";
        self.set(key, n);
      });
      f.appendChild(inp);
      return f;
    }
    function trackingRange(key, labelText) {
      var trLim = (spec.limits && spec.limits[key]) || { min: 0, max: 0.15, step: 0.005 };
      var min = trLim.min != null ? trLim.min : 0;
      var max = trLim.max != null ? trLim.max : 0.15;
      var step = trLim.step || 0.005;
      var val = s[key];
      if (val == null) val = (spec.defaults && spec.defaults[key] != null) ? spec.defaults[key] : min;
      var num = Number(val);
      if (isNaN(num)) num = min;   // a valid 0 (no tracking) must survive
      val = Math.max(min, Math.min(max, num));
      s[key] = val;

      var f = el("div", "swell-pz-field swell-pz-field--size");
      var l = el("label");
      l.textContent = labelText;
      var valSpan = el("span", "swell-pz-size-val");
      valSpan.textContent = Math.round(val * 1000) / 10 + "%";
      l.appendChild(valSpan);
      f.appendChild(l);

      var inp = el("input");
      inp.type = "range";
      inp.min = String(min);
      inp.max = String(max);
      inp.step = String(step);
      inp.value = String(val);
      inp.className = "swell-pz-range";
      inp.addEventListener("input", function () {
        var n = parseFloat(inp.value);
        valSpan.textContent = Math.round(n * 1000) / 10 + "%";
        self.set(key, n);
      });
      f.appendChild(inp);
      return f;
    }

    function section(title) {
      var sec = el("div", "swell-pz-section");
      var h = el("div", "swell-pz-section__title");
      h.textContent = title;
      sec.appendChild(h);
      return sec;
    }
    function row2(a, b) {
      var r = el("div", "swell-pz-row swell-pz-row--tight");
      r.appendChild(a);
      if (b) r.appendChild(b);
      return r;
    }

    // Size + placement on one row
    var sizeIds = spec.size_ids || spec.ratios || [];
    var sizeItems = sizeIds.map(function (id) {
      var info = (spec.sizes && spec.sizes[id]) || (spec.print && spec.print[id]) || {};
      return { id: id, label: info.label || id };
    });
    // Size + Style on the top row
    var rowTop = el("div", "swell-pz-row swell-pz-row--tight");
    var fSize = field("Size");
    var sizeSel = el("select");
    sizeItems.forEach(function (it) {
      var o = el("option"); o.value = it.id; o.textContent = it.label;
      if (it.id === s.size) o.selected = true;
      sizeSel.appendChild(o);
    });
    sizeSel.addEventListener("change", function () { self.setSize(sizeSel.value); });
    fSize.appendChild(sizeSel);
    rowTop.appendChild(fSize);
    var styleItems = self.getStylesForSize(s.size || sizeIds[0]);
    if (styleItems.length > 1) {
      var fStyle = field("Style");
      var styleSel = el("select");
      styleItems.forEach(function (st) {
        var o = el("option"); o.value = st; o.textContent = st;
        if (st === s.style) o.selected = true;
        styleSel.appendChild(o);
      });
      styleSel.addEventListener("change", function () { self.set("style", styleSel.value); });
      fStyle.appendChild(styleSel);
      rowTop.appendChild(fStyle);
    }
    this.controls.appendChild(rowTop);

    // ── Design Options ──────────────────────────────────────────────────────
    // Grouped directly beneath Size/Style: Text Placement + Gap from heading on
    // one row, Shadow gradient + Matte border toggles on the next — a symmetric
    // two-column block. (Gap and the toggles used to live in Subheading/Options.)
    var secDesign = section("Design Options");
    var designRow = el("div", "swell-pz-row swell-pz-row--tight");
    if (spec.placements && spec.placements.length > 1) {
      var fPl = field("Text Placement");
      fPl.appendChild(select("placement", spec.placements.map(function (p) {
        return { id: p, label: p === "top" ? "Top" : "Bottom" };
      }), "label", "id"));
      designRow.appendChild(fPl);
    }
    if (spec.limits && spec.limits.block_gap_px) {
      designRow.appendChild(fontSizeRange("block_gap_px", "Gap from heading"));
    }
    if (designRow.children.length) secDesign.appendChild(designRow);

    // Display toggles: Shadow gradient (left) and Matte border (right). The
    // matte swatches stack under the matte toggle so they stay associated.
    var togRow = el("div", "swell-pz-row swell-pz-row--tight swell-pz-row--toggles");
    var scrimTog = el("label", "swell-pz-toggle");
    var scrimCb = el("input"); scrimCb.type = "checkbox"; scrimCb.checked = s.scrim !== false;
    scrimCb.addEventListener("change", function () { self.set("scrim", scrimCb.checked); });
    var scrimLbl = el("span"); scrimLbl.textContent = "Shadow gradient";
    scrimTog.appendChild(scrimCb); scrimTog.appendChild(scrimLbl);
    togRow.appendChild(scrimTog);

    if (opts.matte && opts.matte.available) {
      var matteGroup = el("div", "swell-pz-matte-group");
      var matteTog = el("label", "swell-pz-toggle");
      var cb = el("input"); cb.type = "checkbox"; cb.checked = !!s.matte;
      var matteSw = el("div", "swell-pz-swatches swell-pz-swatches--inline");
      opts.matte.colors.forEach(function (c) {
        var b = el("button", "swell-pz-swatch");
        b.type = "button";
        b.style.background = c.hex; b.title = c.label;
        b.setAttribute("aria-pressed", String(s.matte_color === c.id));
        b.addEventListener("click", function () {
          self.set("matte_color", c.id);
          matteSw.querySelectorAll(".swell-pz-swatch").forEach(function (x) { x.setAttribute("aria-pressed", "false"); });
          b.setAttribute("aria-pressed", "true");
        });
        matteSw.appendChild(b);
      });
      matteSw.style.display = s.matte ? "" : "none";
      cb.addEventListener("change", function () {
        self.set("matte", cb.checked);
        matteSw.style.display = cb.checked ? "" : "none";
      });
      var matteLbl = el("span"); matteLbl.textContent = "Matte border";
      matteTog.appendChild(cb); matteTog.appendChild(matteLbl);
      matteGroup.appendChild(matteTog);
      matteGroup.appendChild(matteSw);
      togRow.appendChild(matteGroup);
    }
    secDesign.appendChild(togRow);
    this.controls.appendChild(secDesign);

    // Heading block
    var secH = section("Heading");
    var fHead = field("Text");
    fHead.appendChild(textInput("heading", spec.limits.heading_max_chars));
    secH.appendChild(fHead);
    // Font + Case on one row
    var rowHFont = el("div", "swell-pz-row swell-pz-row--font");
    var fHFont = field("Font");
    fHFont.appendChild(fontControl("heading"));
    rowHFont.appendChild(fHFont);
    var fHCase = field("Case");
    fHCase.className = "swell-pz-field swell-pz-field--compact";
    var hCaseSel = el("select");
    [{ id: "upper", label: "ABC" }, { id: "title", label: "Abc" }, { id: "lower", label: "abc" }].forEach(function (c) {
      var o = el("option"); o.value = c.id; o.textContent = c.label;
      if ((s.heading_case || "upper") === c.id) o.selected = true;
      hCaseSel.appendChild(o);
    });
    hCaseSel.addEventListener("change", function () { self.set("heading_case", hCaseSel.value); });
    fHCase.appendChild(hCaseSel);
    rowHFont.appendChild(fHCase);
    secH.appendChild(rowHFont);
    // Color
    var fHCol = field("Color");
    fHCol.appendChild(swatches("heading_color", "heading_custom_hex", opts.heading_colors));
    secH.appendChild(fHCol);
    // Size + Letter spacing sliders
    secH.appendChild(row2(
      fontSizeRange("heading_font_size_px", "Size"),
      trackingRange("heading_tracking", "Letter spacing")
    ));
    this.controls.appendChild(secH);

    // Subheading block
    var secS = section("Subheading");
    var fSub = field("Text");
    fSub.appendChild(textInput("subheading", spec.limits.subheading_max_chars));
    secS.appendChild(fSub);
    // Font + Case on one row
    var rowSFont = el("div", "swell-pz-row swell-pz-row--font");
    var fSFont = field("Font");
    fSFont.appendChild(fontControl("subheading"));
    rowSFont.appendChild(fSFont);
    var fSCase = field("Case");
    fSCase.className = "swell-pz-field swell-pz-field--compact";
    var sCaseSel = el("select");
    [{ id: "upper", label: "ABC" }, { id: "title", label: "Abc" }, { id: "lower", label: "abc" }].forEach(function (c) {
      var o = el("option"); o.value = c.id; o.textContent = c.label;
      if ((s.subheading_case || "upper") === c.id) o.selected = true;
      sCaseSel.appendChild(o);
    });
    sCaseSel.addEventListener("change", function () { self.set("subheading_case", sCaseSel.value); });
    fSCase.appendChild(sCaseSel);
    rowSFont.appendChild(fSCase);
    secS.appendChild(rowSFont);
    // Color
    var fSCol = field("Color");
    fSCol.appendChild(swatches("subheading_color", "subheading_custom_hex", opts.subheading_colors));
    secS.appendChild(fSCol);
    // Size + Letter spacing sliders (gap moved up to Design Options)
    secS.appendChild(row2(
      fontSizeRange("subheading_font_size_px", "Size"),
      trackingRange("subheading_tracking", "Letter spacing")
    ));
    this.controls.appendChild(secS);
  };

  Editor.prototype.getBase = function (ratio) {
    var self = this;
    if (this.baseCache[ratio]) return Promise.resolve(this.baseCache[ratio]);
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () { self.baseCache[ratio] = img; resolve(img); };
      img.onerror = function () { reject(new Error("base art missing for " + ratio)); };
      // Cache-buster: _v changes per deploy, base_rev changes whenever the
      // product's art is re-onboarded (Update Art / Update Existing), so updated
      // art busts the browser/CDN cache without a redeploy. Falls back to a
      // per-load timestamp when neither is present.
      var bust = self.spec.base_rev || self.spec._v || Date.now();
      img.src = self.proxyBase + "/base?handle=" + encodeURIComponent(self.handle) +
        "&ratio=" + encodeURIComponent(ratio) + "&_v=" + encodeURIComponent(bust);
    });
  };

  Editor.prototype.render = function () {
    var self = this, spec = this.spec, s = this.state;
    var ratio = ratioForState(spec, s);
    var aspect = previewAspect(spec, s);
    var cw = 600, ch = Math.round(cw / aspect);
    this.canvas.width = cw; this.canvas.height = ch;
    var ctx = this.canvas.getContext("2d");
    this.applyFrame();
    // Token guards async callbacks: if a newer render() supersedes this one,
    // stale paint/repaint callbacks below no-op instead of clobbering the canvas.
    var token = (this._renderToken = (this._renderToken || 0) + 1);

    this.statusEl.textContent = "";
    this.showLoader("Loading preview…");
    // Wait for the base art AND the fonts the current selection needs. Canvas
    // text never lazy-loads fonts, so painting before they're ready would fall
    // back to sans-serif with no automatic repaint.
    Promise.all([this.getBase(ratio), this.ensureFonts()]).then(function (res) {
      if (token !== self._renderToken) return;
      var img = res[0];
      self.paint(ctx, img, cw, ch);
      self.hideLoader();
      self.addBtn.disabled = false;
      self.repaintWhenFontsReady(token, ctx, img, cw, ch);
    }).catch(function (e) {
      ctx.clearRect(0, 0, cw, ch);
      ctx.fillStyle = "#eee"; ctx.fillRect(0, 0, cw, ch);
      self.hideLoader();
      self.statusEl.textContent = e.message;
    });
  };

  // Mobile browsers sometimes resolve document.fonts.load() before the face is
  // actually usable for canvas text. If we paint then, fitText measures with the
  // fallback (wider) font and picks a smaller heading size — which collapses the
  // apparent gap between heading and subheading vs desktop. So if any needed face
  // isn't ready yet, repaint exactly once when document.fonts.ready settles.
  Editor.prototype.repaintWhenFontsReady = function (token, ctx, img, cw, ch) {
    if (!document.fonts || !document.fonts.check || !document.fonts.ready) return;
    var self = this;
    var allReady = this.fontFaceDescs().every(function (d) {
      try { return document.fonts.check(d); } catch (e) { return true; }
    });
    if (allReady) return;
    document.fonts.ready.then(function () {
      if (token !== self._renderToken) return;   // superseded by a newer render
      self.paint(ctx, img, cw, ch);
    }).catch(function () {});
  };

  Editor.prototype.applyFrame = function () {
    var fs = frameSpec(this.state && this.state.style);
    var c = this.frame || this.canvas;
    c.setAttribute("data-frame-kind", fs.kind);
    c.setAttribute("data-frame-tone", fs.tone);
  };

  // CSS font descriptors ("700 32px 'SWKiona'") for the fonts the current state
  // actually uses. document.fonts.load() resolves once the matching face has
  // downloaded, so render() can guarantee the right glyphs before painting.
  Editor.prototype.fontFaceDescs = function () {
    var s = this.state, fonts = this.spec.options.fonts, out = [];
    function add(fontId, weightId) {
      if (!fontId) return;
      out.push(weightNumFor(fonts, fontId, weightId) + " 32px '" + familyOf(fonts, fontId) + "'");
    }
    add(s.heading_font, s.heading_font_weight);
    add(s.subheading_font, s.subheading_font_weight);
    return out;
  };
  Editor.prototype.ensureFonts = function () {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    var loads = this.fontFaceDescs().map(function (d) {
      try { var p = document.fonts.load(d); return p && p.catch ? p.catch(function () {}) : Promise.resolve(); }
      catch (e) { return Promise.resolve(); }
    });
    // Await the specific faces AND fonts.ready — the latter closes a mobile race
    // where load() resolves before the face is usable for canvas measurement.
    return Promise.all(loads).then(function () {
      return document.fonts.ready ? document.fonts.ready.catch(function () {}) : null;
    }).catch(function () {});
  };

  Editor.prototype.paint = function (ctx, img, W, H) {
    var spec = this.spec, s = this.state;
    var placement = s.placement || spec.default_placement;
    var layout = spec.layout[placement];
    var opts = spec.options;
    var typo = scaledTypo(spec, H, s);
    var boxLeft = typo.boxX * W;
    var boxW = typo.boxW * W;

    var off = document.createElement("canvas"); off.width = W; off.height = H;
    var octx = off.getContext("2d");
    octx.drawImage(img, 0, 0, W, H);

    var scrim = layout.scrim;
    if (s.scrim !== false && scrim && scrim.enabled) {
      var rx = scrim.rect.x * W, ry = scrim.rect.y * H, rw = scrim.rect.w * W, rh = scrim.rect.h * H;
      var g = octx.createLinearGradient(0, ry, 0, ry + rh);
      var maxA = scrim.max_opacity != null ? scrim.max_opacity : 0.45;
      if (scrim.gradient === "vertical-flip") { g.addColorStop(0, "rgba(0,0,0," + maxA + ")"); g.addColorStop(1, "rgba(0,0,0,0)"); }
      else { g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, "rgba(0,0,0," + maxA + ")"); }
      octx.fillStyle = g; octx.fillRect(rx, ry, rw, rh);
    }

    function applyCase(text, caseId) {
      if (caseId === "lower") return text.toLowerCase();
      if (caseId === "title") return text.replace(/\w\S*/g, function (w) { return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); });
      return text.toUpperCase();
    }

    var regions = {};   // role -> {x,y,w,h} in offscreen (pre-matte) space

    function drawLine(text, fontId, weightId, hex, startSize, minSize, trackingFrac, align, caseId, y, role) {
      if (!text) return 0;
      var content = applyCase(text, caseId);
      var family = familyOf(opts.fonts, fontId);
      var weight = weightNumFor(opts.fonts, fontId, weightId);
      var fit = fitText(octx, content, family, weight, boxW, startSize, minSize, trackingFrac);
      var box = drawTracked(octx, content, family, weight, fit.size, fit.tracking, hex, align || "center", boxLeft, boxW, y);
      if (role && box) regions[role] = box;
      return fit.size * 1.15;
    }

    // Draw the heading, wrapping onto up to headingMaxLines rows; returns the
    // block height and a union bounding box (for the click-to-edit hit region).
    function headingLayout(text, fontId, weightId) {
      var content = applyCase(text, headCase);
      var family = familyOf(opts.fonts, fontId);
      var weight = weightNumFor(opts.fonts, fontId, weightId);
      var lay = layoutHeading(octx, content, family, weight, boxW, typo.headingStart, typo.headingMin,
        typo.headingTracking, typo.headingMaxLines);
      var advance = lay.size * typo.headingLineSpacing;
      var height = (lay.lines.length - 1) * advance + lay.size * 1.15;
      return { lay: lay, family: family, weight: weight, advance: advance, height: height };
    }
    function drawHeadingBlock(hl, hex, align, yTop) {
      var union = null;
      for (var i = 0; i < hl.lay.lines.length; i++) {
        var box = drawTracked(octx, hl.lay.lines[i], hl.family, hl.weight, hl.lay.size, hl.lay.tracking,
          hex, align || "center", boxLeft, boxW, yTop + i * hl.advance);
        if (!box) continue;
        if (!union) { union = { x: box.x, y: box.y, w: box.w, h: box.h }; }
        else {
          var right = Math.max(union.x + union.w, box.x + box.w);
          var bottom = Math.max(union.y + union.h, box.y + box.h);
          union.x = Math.min(union.x, box.x); union.y = Math.min(union.y, box.y);
          union.w = right - union.x; union.h = bottom - union.y;
        }
      }
      return union;
    }

    var hBox = layout.heading || {};
    var sBox = layout.subheading || {};
    var headHex = resolveColor(s, "heading_color", "heading_custom_hex", opts.heading_colors);
    var subHex = resolveColor(s, "subheading_color", "subheading_custom_hex", opts.subheading_colors);
    var headCase = s.heading_case || "upper";
    var subCase = s.subheading_case || "upper";

    if (placement === "top") {
      var y = typo.margin;
      if (s.heading) {
        var hlTop = headingLayout(s.heading, s.heading_font, s.heading_font_weight);
        regions.heading = drawHeadingBlock(hlTop, headHex, hBox.align, y);
        y += hlTop.height + typo.gap;
      }
      if (s.subheading) {
        drawLine(s.subheading, s.subheading_font, s.subheading_font_weight, subHex, typo.subStart, typo.subMin,
          typo.subTracking, sBox.align, subCase, y, "subheading");
      }
    } else {
      var ySub = H - typo.margin;
      var yHead = ySub;
      if (s.subheading) {
        var subContent = applyCase(s.subheading, subCase);
        var subFamily = familyOf(opts.fonts, s.subheading_font);
        var subWeight = weightNumFor(opts.fonts, s.subheading_font, s.subheading_font_weight);
        var subFit = fitText(octx, subContent, subFamily, subWeight, boxW, typo.subStart, typo.subMin, typo.subTracking);
        var subH = subFit.size * 1.15;
        ySub = H - typo.margin - subH;
        regions.subheading = drawTracked(octx, subContent, subFamily, subWeight, subFit.size, subFit.tracking,
          subHex, sBox.align || "center", boxLeft, boxW, ySub);
        yHead = ySub - typo.gap;
      }
      if (s.heading) {
        var hlBot = headingLayout(s.heading, s.heading_font, s.heading_font_weight);
        yHead = (s.subheading ? ySub : H - typo.margin) - hlBot.height - (s.subheading ? typo.gap : 0);
        regions.heading = drawHeadingBlock(hlBot, headHex, hBox.align, yHead);
      }
    }

    ctx.clearRect(0, 0, W, H);
    // matteOn/b are reused below to transform the click hit-regions into the
    // same visible-canvas space the offscreen art is composited into.
    var matteOn = !!(s.matte && opts.matte);
    var b = 0;
    if (matteOn) {
      var bf = opts.matte.border_frac || 0.05;
      b = Math.round(Math.min(W, H) * bf);
      ctx.fillStyle = hexOf(opts.matte.colors, s.matte_color);
      ctx.fillRect(0, 0, W, H);
      ctx.drawImage(off, b, b, W - 2 * b, H - 2 * b);
    } else {
      ctx.drawImage(off, 0, 0);
    }

    // Record hit-regions in visible-canvas coordinates (apply the matte inset +
    // scale) so onCanvasClick can map a tap to the right text block.
    function toCanvasSpace(box) {
      if (!box) return null;
      if (!matteOn) return box;
      var sx = (W - 2 * b) / W, sy = (H - 2 * b) / H;
      return { x: b + box.x * sx, y: b + box.y * sy, w: box.w * sx, h: box.h * sy };
    }
    this.hitRegions = { heading: toCanvasSpace(regions.heading), subheading: toCanvasSpace(regions.subheading) };
  };

  // Map a canvas tap to a text role and jump to its input. Tapping the heading
  // or subheading in the preview focuses + scrolls to the matching field, which
  // is especially handy on mobile where the fields sit below the fold.
  Editor.prototype.onCanvasClick = function (e) {
    if (!this.hitRegions) return;
    var canvas = this.canvas;
    var rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var x = (e.clientX - rect.left) * (canvas.width / rect.width);
    var y = (e.clientY - rect.top) * (canvas.height / rect.height);
    // Generous vertical padding makes small (e.g. subheading) text easy to tap.
    var padX = canvas.width * 0.02, padY = canvas.height * 0.02;
    var roles = ["heading", "subheading"], hit = null;
    for (var i = 0; i < roles.length; i++) {
      var bx = this.hitRegions[roles[i]];
      if (bx && x >= bx.x - padX && x <= bx.x + bx.w + padX &&
              y >= bx.y - padY && y <= bx.y + bx.h + padY) { hit = roles[i]; break; }
    }
    if (hit) this.focusField(hit);
  };

  Editor.prototype.focusField = function (role) {
    var inp = this.fieldInputs && this.fieldInputs[role];
    if (!inp) return;
    try { inp.scrollIntoView({ behavior: "smooth", block: "center" }); }
    catch (e) { inp.scrollIntoView(); }
    try { inp.focus({ preventScroll: true }); } catch (e) { inp.focus(); }
    try { inp.select(); } catch (e) { /* ignore */ }
    var field = inp.closest ? inp.closest(".swell-pz-field") : null;
    if (field) {
      field.classList.remove("swell-pz-field--flash");
      void field.offsetWidth;                 // restart the flash animation
      field.classList.add("swell-pz-field--flash");
    }
  };

  Editor.prototype.uploadPreview = function () {
    var self = this;
    return new Promise(function (resolve, reject) {
      try {
        var dataUrl = self.canvas.toDataURL("image/jpeg", 0.88);
      } catch (e) {
        reject(new Error("Could not capture preview"));
        return;
      }
      fetch(self.proxyBase + "/preview/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: dataUrl })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok || !res.j.ok) throw new Error(res.j.error || "Preview upload failed");
          if (res.j.url) {
            resolve(res.j.url);
            return;
          }
          resolve(absoluteProxyUrl(self.proxyBase, res.j.path));
        })
        .catch(reject);
    });
  };

  Editor.prototype.addToCart = function () {
    var self = this;
    this.errorEl.textContent = "";
    this.addBtn.disabled = true;

    fetch(this.proxyBase + "/validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ handle: this.handle, config: this.state })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok || !res.j.ok) throw new Error(res.j.error || "Invalid configuration");
        var clean = res.j.config;
        clean.handle = self.handle;
        clean.product_key = self.spec.product_key;
        return self.uploadPreview().then(function (previewUrl) {
          return { clean: clean, previewUrl: previewUrl };
        });
      })
      .then(function (ctx) {
        var clean = ctx.clean;
        var sizeKey = self.state.size || self.state.ratio;
        var styleKey = self.state.style || "Print Only";
        var sizeEntry = self.variantMap[sizeKey];
        var variantId;
        if (sizeEntry && typeof sizeEntry === "object" && !Array.isArray(sizeEntry)) {
          variantId = sizeEntry[styleKey] || sizeEntry["Print Only"] || Object.values(sizeEntry)[0];
        } else {
          variantId = sizeEntry || self.fallbackVariant;
        }
        if (!variantId) throw new Error("No variant configured for size " + sizeKey + " / style " + styleKey);

        var props = {};
        props["_pz_template"] = clean.template;
        props["_pz_config"] = JSON.stringify(clean);
        props["Preview"] = ctx.previewUrl;
        props["_pz_style"] = styleKey;

        var cartUi = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        var payload = { items: [{ id: Number(variantId), quantity: 1, properties: props }] };
        if (cartUi && typeof cartUi.getSectionsToRender === "function") {
          payload.sections = cartUi.getSectionsToRender().map(function (s) { return s.id; }).join(",");
          payload.sections_url = window.location.pathname;
        }
        return fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      })
      .then(function (r) {
        if (!r || !r.ok) return r.json().then(function (j) { throw new Error(j.description || "Add to cart failed"); });
        return r.json();
      })
      .then(function (response) {
        if (response.status) throw new Error(response.description || response.message || "Add to cart failed");
        var cartUi = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        if (cartUi && typeof cartUi.renderContents === "function") {
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "swell-personalizer", cartData: response });
          }
          cartUi.renderContents(response);
          if (cartUi.classList.contains("is-empty")) cartUi.classList.remove("is-empty");
          self.close();
          return;
        }
        window.location.href = (window.routes && window.routes.cart_url) || "/cart";
      })
      .catch(function (e) { self.errorEl.textContent = e.message; })
      .finally(function () { self.addBtn.disabled = false; });
  };

  // ── Site-wide "Personalize" search launcher ────────────────────────────────
  //
  // A header/hero CTA (rendered by the app-embed block) carries
  // [data-swell-personalize-launch]. Clicking it opens a search modal that lists
  // ONLY personalizable products (from GET /catalog), filters as the customer
  // types, and deep-links the chosen product into its editor via ?customize=1.
  function SearchModal(proxyBase) {
    this.proxyBase = proxyBase || "/apps/personalizer";
    this.catalog = null;        // full list once fetched
    this.fetching = null;       // in-flight promise
    this.built = false;
    this.PAGE_SIZE = 30;        // rows appended per "page" (infinite scroll)
    this._matches = [];         // current filtered list
    this._rendered = 0;         // how many rows are currently in the DOM
  }

  SearchModal.prototype.build = function () {
    if (this.built) return;
    var self = this;
    var overlay = el("div", "swell-pz-modal swell-pz-search");
    overlay.setAttribute("data-swell-search-overlay", "");
    overlay.hidden = true;
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Personalize a print");

    var panel = el("div", "swell-pz-search__panel");
    var header = el("div", "swell-pz-search__header");
    var title = el("h2", "swell-pz-search__title");
    title.textContent = "Personalize a print";
    var close = el("button", "swell-pz-search__close");
    close.setAttribute("type", "button");
    close.setAttribute("aria-label", "Close");
    close.innerHTML = "&times;";
    header.appendChild(title);
    header.appendChild(close);

    var searchWrap = el("div", "swell-pz-search__field");
    var input = el("input", "swell-pz-search__input");
    input.setAttribute("type", "search");
    input.setAttribute("placeholder", "Search a place — e.g. Maui, Boca Grande, Napa…");
    input.setAttribute("autocomplete", "off");
    searchWrap.appendChild(input);

    var status = el("div", "swell-pz-search__status");
    var results = el("ul", "swell-pz-search__results");
    results.setAttribute("data-swell-search-results", "");

    panel.appendChild(header);
    panel.appendChild(searchWrap);
    panel.appendChild(status);
    panel.appendChild(results);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    this.overlay = overlay;
    this.input = input;
    this.results = results;
    this.status = status;

    close.addEventListener("click", function () { self.close(); });
    overlay.addEventListener("click", function (e) { if (e.target === overlay) self.close(); });
    document.addEventListener("keydown", function (e) {
      if (!overlay.hidden && e.key === "Escape") self.close();
    });
    var t = null;
    input.addEventListener("input", function () {
      clearTimeout(t);
      t = setTimeout(function () { self.renderResults(input.value); }, 80);
    });
    // Infinite scroll: append the next page as the list nears the bottom.
    results.addEventListener("scroll", function () {
      if (results.scrollTop + results.clientHeight >= results.scrollHeight - 240) {
        self._renderMore();
      }
    });
    this.built = true;
  };

  SearchModal.prototype.open = function () {
    var self = this;
    this.build();
    this.overlay.hidden = false;
    document.documentElement.classList.add("swell-pz-search-open");
    this.input.value = "";
    setTimeout(function () { self.input.focus(); }, 30);
    this.status.textContent = "Loading destinations…";
    this.load().then(function () {
      self.renderResults("");
    }).catch(function () {
      self.status.textContent = "Couldn't load personalizable products. Please try again.";
    });
  };

  SearchModal.prototype.close = function () {
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.classList.remove("swell-pz-search-open");
  };

  SearchModal.prototype.load = function () {
    var self = this;
    if (this.catalog) return Promise.resolve(this.catalog);
    if (this.fetching) return this.fetching;
    this.fetching = fetch(this.proxyBase + "/catalog")
      .then(function (r) { if (!r.ok) throw new Error("catalog " + r.status); return r.json(); })
      .then(function (d) { self.catalog = (d && d.products) || []; return self.catalog; });
    return this.fetching;
  };

  SearchModal.prototype.filter = function (q) {
    var list = this.catalog || [];
    q = (q || "").trim().toLowerCase();
    if (!q) return list;
    var terms = q.split(/\s+/);
    return list.filter(function (p) {
      var hay = (p.heading + " " + p.subheading + " " + p.handle).toLowerCase();
      return terms.every(function (t) { return hay.indexOf(t) >= 0; });
    });
  };

  SearchModal.prototype.renderResults = function (q) {
    if (!this.catalog) return;
    this._matches = this.filter(q);
    this._rendered = 0;
    this.results.innerHTML = "";
    this.results.scrollTop = 0;
    if (!this._matches.length) {
      this.status.textContent = q ? "No matching destinations." : "No personalizable products yet.";
      return;
    }
    this._renderMore();
  };

  // Append the next page of rows. Called on first render and as the list is
  // scrolled near the bottom, so the customer can browse the entire catalog.
  SearchModal.prototype._renderMore = function () {
    var self = this;
    var total = this._matches.length;
    if (this._rendered >= total) return;
    var next = this._matches.slice(this._rendered, this._rendered + this.PAGE_SIZE);
    var frag = document.createDocumentFragment();
    next.forEach(function (p) { frag.appendChild(self.renderRow(p)); });
    this.results.appendChild(frag);
    this._rendered += next.length;
    var count = total + (total === 1 ? " destination" : " destinations");
    this.status.textContent = this._rendered < total
      ? (count + " — scroll for more")
      : count;
    // If the list doesn't yet overflow (tall viewport), keep filling so the
    // scroll handler has room to trigger; stops when it overflows or exhausts.
    if (this._rendered < total && this.results.scrollHeight <= this.results.clientHeight) {
      this._renderMore();
    }
  };

  SearchModal.prototype.renderRow = function (p) {
    var li = el("li", "swell-pz-search__result");
    var a = el("a", "swell-pz-search__link");
    a.setAttribute("href", "/products/" + encodeURIComponent(p.handle) + "?customize=1");

    var thumb = el("span", "swell-pz-search__thumb");
    var img = el("img");
    img.setAttribute("loading", "lazy");
    img.setAttribute("alt", "");
    img.setAttribute("src", this.proxyBase + "/base?handle=" + encodeURIComponent(p.handle)
      + "&ratio=" + encodeURIComponent(p.default_ratio || "2x3")
      + (p.base_rev ? "&_v=" + encodeURIComponent(p.base_rev) : ""));
    img.addEventListener("error", function () { thumb.classList.add("swell-pz-search__thumb--empty"); });
    thumb.appendChild(img);

    var meta = el("span", "swell-pz-search__meta");
    var h = el("span", "swell-pz-search__heading");
    h.textContent = p.heading || p.handle;
    var s = el("span", "swell-pz-search__sub");
    s.textContent = p.subheading || "";
    meta.appendChild(h);
    if (p.subheading) meta.appendChild(s);

    a.appendChild(thumb);
    a.appendChild(meta);
    li.appendChild(a);
    return li;
  };

  function bootLauncher() {
    var triggers = document.querySelectorAll("[data-swell-personalize-launch]");
    if (!triggers.length) return;
    var base = triggers[0].getAttribute("data-proxy-base") || "/apps/personalizer";
    var modal = new SearchModal(base);
    triggers.forEach(function (t) {
      if (t.__swellLaunchBound) return;
      t.__swellLaunchBound = true;
      t.addEventListener("click", function (e) { e.preventDefault(); modal.open(); });
    });
  }

  function boot() {
    document.querySelectorAll("[data-swell-personalizer]").forEach(function (root) {
      if (root.__swellBooted) return;
      root.__swellBooted = true;
      new Editor(root).init();
    });
    bootLauncher();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
